import React, { useRef, useEffect, useCallback, useContext, useState } from 'react';
import ReactDOM from 'react-dom';
import Moveable from 'react-moveable';
import { Frame } from 'scenejs';
import { ComplexEditingContext } from '../../../context/complexEditingContext';
import { updateSvgValues } from '../../../utils/svgUtils';
import { ALL_FILLABLE_ELEMENTS, FILLABLE_CHECKBOX_GROUP_TYPE, FILLABLE_CHECKBOX_TYPE, FILLABLE_COMBO_BOX_TYPE, FILLABLE_COMBOBOX_GROUP_TYPE, FILLABLE_RADIO_BUTTON_GROUP_TYPE, FILLABLE_RADIO_BUTTON_TYPE, FILLABLE_SIGNATURE_TYPE, FILLABLE_TEXT_INPUT_TYPE, TEXT_ELEMENT } from '../../../utils/complexDocumentConstants';
import { PageElementsContext } from '../../../context/pageElementsContext';
import AccessibilityIcon from '../../../components/Accessibility/AccessibilityIcon';

const keepRatioNotElements = [
  FILLABLE_TEXT_INPUT_TYPE,
  FILLABLE_COMBO_BOX_TYPE,
  FILLABLE_SIGNATURE_TYPE,
  FILLABLE_CHECKBOX_GROUP_TYPE,
  FILLABLE_RADIO_BUTTON_GROUP_TYPE,
  FILLABLE_COMBOBOX_GROUP_TYPE,
];
const keepRatioElements = [FILLABLE_CHECKBOX_TYPE, FILLABLE_RADIO_BUTTON_TYPE];

const GridContentResizer = ({ height, width, onResizeStop, className, dependentRef, resizeCallback, cellRef, handle = ['nw','n','ne','w','e','sw','s','se'], enableResize = true }, moveableRef) => {
  const frameMap = useRef(new Map());
  const widthRef = useRef(null);

  const imageRef = useContext(ComplexEditingContext);
  const { updatedElementStyles } = useContext(PageElementsContext);
  const [showAccessibility, setShowAccessibility] = useState(false);
  const [accessibilityPopupVisible, setAccessibilityPopupVisible] = useState(false);

  const newFrame = (el, save = true) => {
    const height = `${el.offsetHeight}px`;
    const width = `${el.offsetWidth}px`;
    const actualHeightStyle = el.style.height;
    const actualWidthStyle = el.style.width;
    const frame = new Frame({
      height: save ? height : actualHeightStyle, // To retain the relative height in Calc instead of fixed pixels
      width: save ? width : actualWidthStyle
    });

    if (save) {
      frameMap.current.set(el, frame);
    }
    return frame;
  };

  const getFrame = useCallback((target) => {
    return frameMap.current.get(target) || newFrame(target);
  }, [])

  useEffect(() => {
    if (moveableRef.current) moveableRef.current.updateRect();
    if (frameMap.current) frameMap.current.clear();
  }, [height, width, moveableRef]);

  const setViewPort = (target, override = false) => {
      const { offsetWidth, offsetHeight } = target;
      console.log("Setting view port :: ", target);
      // Check if viewport of element is calculated
      if (override || !target.getAttribute('data-viewport')) {
        if (target.style && target.style.width) {
          const targetWidth = target.style.width;
          if (targetWidth === '100%' || targetWidth.includes('%')) {
            target.setAttribute('data-viewport', offsetWidth);
            target.setAttribute('data-viewheight', offsetHeight);
          } else {
            target.setAttribute('data-viewport', Number(targetWidth.replace(/px$/, '')));
            target.setAttribute('data-viewheight', offsetHeight);
          }
        } else if (offsetWidth && offsetHeight) {
          target.setAttribute('data-viewport', offsetWidth);
          target.setAttribute('data-viewheight', offsetHeight);
        }
        if (target &&
              target.getAttribute('data-element-type') &&
              target.getAttribute('data-element-type') === 'rectangle') {
          const childNode = target.children[0].children[0];
          target.setAttribute('data-border-width', childNode.getAttribute('data-border-width'));
          target.setAttribute('data-border-radius', childNode.getAttribute('data-border-radius'));
        }
      }
  };

  useEffect(() => {
    if (enableResize) {
      let selectableTarget = cellRef.current?.querySelector('.selectable');
      if (selectableTarget) setViewPort(selectableTarget, true);
    }
  }, [enableResize, updatedElementStyles]);

  const handleRender = useCallback(({ target }, isGroup = false) => {
    target.style.cssText += getFrame(target).toCSS();
  }, []);

  const handleMoveableResize = useCallback(({ target, width, height, direction }) => {
    const frame = getFrame(target);
    let newWidth = width;
    let newHeight = height;
    positionAccessibilityIcon();
    if (target &&
      target.getAttribute('data-element-type') &&
      (target.getAttribute('data-element-type') === 'rectangle' || target.getAttribute('data-element-type') === 'circle'|| target.getAttribute('data-element-type') === 'line' || target.getAttribute('data-element-type') === 'doubleLine')) {
      let svgElement = target.childNodes[0]?.childNodes[0];

      const currentSvgJson = imageRef.current.get(target.getAttribute('data-uuid'))?.json;
      let scaling;
      const oldWidth = Number(target.getAttribute('data-viewport'));
      const oldHeight = Number(target.getAttribute('data-viewheight'));
      if (direction[1] !== 0 && direction[0] !== 0) {
        if (
          (direction[0] === 1 && direction[1] === 1) ||
          (direction[0] === 1 && direction[1] === -1)
        ) {
          scaling = height / oldHeight;
        } else {
          scaling = width / oldWidth;
        }
        frame.set('height', `${height}px`);
        frame.set('width', `${width}px`);
      } else {
        if (direction[0] === 0) {
          scaling = height / oldHeight;
        } else if (direction[1] === 0) {
          scaling = width / oldWidth;
        }
      }
      if (svgElement) {
        if (direction[0] === 0) {
          newHeight = height;
          newWidth = oldWidth;
          frame.set('height', `${height}px`);
        }
        if (direction[1] === 0) {
          newWidth = width;
          newHeight = oldHeight;
          frame.set('width', `${width}px`);
        }
        const { stringSVG, parsedString } = updateSvgValues({
            width: newWidth,
            height: newHeight,
            scaling: direction[1] !== 0 && direction[0] !== 0,
            scale: scaling,
            borderWidth: Number(target.getAttribute('data-border-width')),
            borderRadius: Number(target.getAttribute('data-border-radius')),
            staticTableElement: true
          },
          {
            elementDetails: {
              style: {},
              value: currentSvgJson,
              uiType: target.getAttribute('data-element-type'),
            }
          },
          'style'
        );
        target.childNodes[0].innerHTML = stringSVG;
        imageRef.current.set(target.getAttribute('data-uuid'), { json: parsedString, svg: stringSVG });
      }
    } else {
      frame.set('height', `${height.toFixed(3)}px`);
      frame.set('width', `${width.toFixed(3)}px`);
    }
  }, []);

  const positionAccessibilityIcon = () => {
    const icon = document.getElementsByClassName('accessibility-icon')[0];
    if (icon) {
      const elementWidth = moveableRef?.current?.refTargets[0].offsetWidth;
      icon.style.left = `${(elementWidth/2) -18}px`;
    }
  }
  
  useEffect(() => {
    setAccessibilityPopupVisible(false);
    if(!selectableTarget) return;
    if (
      moveableRef.current &&
      selectableTarget.getAttribute('data-element-type') !== TEXT_ELEMENT
    ) {
      const controlBox = moveableRef.current.moveable.controlBox;
      if (controlBox) {
        setShowAccessibility(true);
        requestAnimationFrame(() => {
          positionAccessibilityIcon();
        });
      }
    }
  }, [moveableRef.current]);

  const updateDimensions = useCallback(({ target }) => {
    const frame = getFrame(target);
    const targetElementType = target?.getAttribute('data-element-type');
    const newWidth = parseFloat(frame.get('width').replaceAll('px', ''));
    const newHeight = parseFloat(frame.get('height').replaceAll('px', ''));
    const container = cellRef.current
    const containerWidth = container.offsetWidth;
    const percentageOccupied= (newWidth / containerWidth) * 100;

    let extraChanges = {};

    if (imageRef.current.get(target.getAttribute('data-uuid'))) {
      extraChanges = {
        url: imageRef.current.get(target.getAttribute('data-uuid')).svg
      };
    }

    const changes = { width: percentageOccupied, extraChanges };
    if (ALL_FILLABLE_ELEMENTS.includes(targetElementType)) {
      changes.height = newHeight;
    }

    if (ALL_FILLABLE_ELEMENTS.includes(targetElementType)) {
      if (keepRatioElements.includes(targetElementType)) {
        target.style.width = `${newHeight}px`;
        changes.width = newHeight;
      } else {
        target.style.width = `${percentageOccupied}%`;
      }
      target.style.height = newHeight;
    } else if(['line', 'doubleLine'].includes(targetElementType)){
      target.style.width = `${percentageOccupied}%`;
      target.style.height = newHeight;
    } else {
      target.style.width = `${percentageOccupied}%`;
      target.style.height = 'auto';
    }
    target.setAttribute('data-viewport', newWidth);
    target.setAttribute('data-viewheight', newHeight);
    onResizeStop(changes);
    // Removing all values from map to start afresh
    frameMap.current.clear();
  }, []);

  const setInitialValue = useCallback(({ target, setOrigin }) => {
    const frame = newFrame(target, false);
  
    widthRef.current = {
      staticEl: frame,
    };
    
  }, []);

  let selectableTarget = cellRef.current?.querySelector('.selectable');
  const targetElementType = selectableTarget?.getAttribute('data-element-type');
  const keepRatio = !keepRatioNotElements.includes(targetElementType);
  if (!enableResize || !selectableTarget || targetElementType === 'text') {
    return <div />;
  }

  const getMoveableClassname = targets => {
    if (targets && targets.length === 1) {
      if (
        targets[0] &&
        targets[0].getAttribute('data-element-type') &&
        (targets[0].getAttribute('data-element-type') === 'line' ||
          targets[0].getAttribute('data-element-type') === 'doubleLine')
      ) {
        return `line-element-controls`;
      }
    }
    return className;
  };  

  return (
    <>
    {showAccessibility && moveableRef?.current?.moveable && ReactDOM.createPortal(<AccessibilityIcon visible={accessibilityPopupVisible} setAccessibilityPopupVisible={setAccessibilityPopupVisible} elementUUID={selectableTarget.getAttribute('data-uuid')} dataElementType={selectableTarget.getAttribute('data-element-type')} staticTableBlock={true} />, moveableRef.current.moveable.controlBox.element)}
    <Moveable
        target={selectableTarget}
        ref={moveableRef}
        className={getMoveableClassname([selectableTarget])}
        padding={{ left: 0, top: 0, right: 0, bottom: 0 }}
        container={null}
        renderDirections={handle}
        origin={false}
        checkInput={false}
        keepRatio={keepRatio}
        draggable={false}
        resizable={true}
        rotatable={false}
        scalable={true}
        snappable={false}
        throttleResize={1}
        onRender={handleRender}
        onResizeStart={setInitialValue}
        onResize={handleMoveableResize}
        onResizeEnd={updateDimensions}
      />
    </>
  )
}

export default React.forwardRef(GridContentResizer);